import { CircularProgress, ListItem, ListItemText } from '@mui/material';
import WarningIcon from 'components/icons/CustomStatuses/WarningIcon';
import GVSwitch from 'components/lib/GVSwitch/GVSwitch';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDifferenceStatusFilters,
  getInspectionId,
  getModuleSmartDiscardQty,
  getSelectedModule,
  inspection,
} from 'store';
import { CustomStatus, useInspectionCustomStatuses } from 'store/queries/organizations/useCustomStatuses';
import { makeStyles } from 'tss-react/mui';
import { useTracker } from 'components/Tracker/TrackerProvider';

// Tooltip explains the real server-side rule (apps/api/src/inspection/difference.service.ts):
// spelling diffs in English dictionaries with spell-check confidence below
// SMART_DISCARD_THRESHOLD (0.1) get bucketed here as likely false positives.
const SMART_DISCARDS_TOOLTIP =
  'To reduce false positives, Verify Compare hides spelling results that are likely valid words, names, brands, or abbreviations.';

const useStyles = makeStyles()(() => {
  return {
    listItem: {
      height: '28px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      flex: '1 0 0',
    },
    enabledSwitch: {
      flexShrink: 0,
    },
    warningIcon: {
      marginTop: '5px',
      width: '15px',
      height: '15px',
    },
    text: {
      marginRight: '7px',
    },
    smartDiscardGroup: {
      display: 'flex',
      alignItems: 'center',
      flex: '1 0 auto',
    },
  };
});

interface DifferenceStatusFiltersProps {
  // Used to track visibility — driven by prop rather than mount lifecycle
  // so it stays correct if the parent ever adds keepMounted or a transition.
  isPopperOpen?: boolean;
}

const DifferenceStatusFilters = ({ isPopperOpen = true }: DifferenceStatusFiltersProps) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const tracker = useTracker();
  const moduleSmartDiscardQty = useSelector(getModuleSmartDiscardQty);
  const selectedModule = useSelector(getSelectedModule);
  const differenceStatusFilters = useSelector(getDifferenceStatusFilters);
  const inspectionId = useSelector(getInspectionId);
  const { data: inspectionCustomStatuses, isLoading } = useInspectionCustomStatuses(inspectionId);
  const statuses = inspectionCustomStatuses?.allStatuses || [];

  const showSmartDiscards = moduleSmartDiscardQty > 0;

  // Fires once per popper open when the Smart Discards row is visible.
  // Ref resets on close so the next open re-fires.
  const hasFiredForCurrentOpenRef = useRef(false);
  useEffect(() => {
    if (!isPopperOpen) {
      hasFiredForCurrentOpenRef.current = false;
      return;
    }
    if (isLoading || !showSmartDiscards || !inspectionId) return;
    if (hasFiredForCurrentOpenRef.current) return;
    hasFiredForCurrentOpenRef.current = true;
    tracker.track({
      name: 'smart-discards-toggle-visible',
      data: { smartDiscardQty: moduleSmartDiscardQty, selectedModule },
    });
  }, [isPopperOpen, isLoading, showSmartDiscards, inspectionId, selectedModule, moduleSmartDiscardQty, tracker]);

  const handleToggle = (statusId: string | 'smartDiscard' | 'unassigned') => {
    if (statusId === 'smartDiscard') {
      // Filter value is inverted: `true` = filtered out, so current value = next visible state.
      const nextEnabled = !!differenceStatusFilters.smartDiscard;
      tracker.track({
        name: 'smart-discards-toggle-changed',
        data: { enabled: nextEnabled, smartDiscardQty: moduleSmartDiscardQty, selectedModule },
      });
    }
    dispatch(
      inspection.actions.setDifferenceViewOptions({
        differenceStatusFilters: { ...differenceStatusFilters, [statusId]: !differenceStatusFilters[statusId] },
      }),
    );
  };

  if (isLoading) {
    return <CircularProgress size={15} />;
  }

  return (
    <div style={{ marginBottom: '14px' }} data-testid="difference_status_filters">
      {showSmartDiscards && (
        <ListItem className={classes.listItem} key={'smartDiscard'} id={`filterToggle-smartDiscard`}>
          <div className={classes.smartDiscardGroup}>
            <span className={classes.text}>Smart Discards ({moduleSmartDiscardQty})</span>
            <GVTooltip side="top" title={SMART_DISCARDS_TOOLTIP}>
              <span>
                <WarningIcon className={classes.warningIcon} />
              </span>
            </GVTooltip>
          </div>
          <GVSwitch
            value={!differenceStatusFilters.smartDiscard}
            checked={!differenceStatusFilters.smartDiscard}
            onChange={() => handleToggle('smartDiscard')}
            className={classes.enabledSwitch}
          />
        </ListItem>
      )}
      {Object.values(statuses).map((status: Partial<CustomStatus>) => (
        <ListItem className={classes.listItem} key={status.id} id={`filterToggle-${status.title}`}>
          <ListItemText primary={status.title} />
          <GVSwitch
            value={!differenceStatusFilters[status.id as string]}
            checked={!differenceStatusFilters[status.id as string]}
            onChange={() => handleToggle(status.id as string)}
            className={classes.enabledSwitch}
          />
        </ListItem>
      ))}
      <ListItem className={classes.listItem} key={'unassigned'} id={`filterToggle-unassigned`}>
        <ListItemText primary={'Unassigned'} />
        <GVSwitch
          value={!differenceStatusFilters.unassigned}
          checked={!differenceStatusFilters.unassigned}
          onChange={() => handleToggle('unassigned')}
          className={classes.enabledSwitch}
        />
      </ListItem>
    </div>
  );
};

export default DifferenceStatusFilters;
